import "./ViewLogData.scss";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faHouse,
  faChartLine,
  faFilter,
  faFileText,
} from "@fortawesome/free-solid-svg-icons";

const ViewLogData = () => {
  const location = useLocation();
  const product = location.state?.product;
  const selectedLogFiles = location.state?.selectedLogFiles;
  const [LogDataLoading, setLogDataLoading] = useState(false);
  const [LogDataArray, setLogDataArray] = useState([]);
  const [showOnlyNonBlankEvents, setShowOnlyNonBlankEvents] = useState(false); // Add state for filtering 'Event' column

  const navigate = useNavigate();

  const EVOColumns = {
    "Date": true,
    "Time": true,

    "Mode": false,
    "Error Code": false,
    "Charge Stage": false,
    //"CAN State": false, // EVO SP only
    "Event": true,

    "Generator Status [mask]": false,// EVO only
    "Generator Frequency": false,// EVO only
    "Generator Voltage": false,// EVO only

    "Grid Status": false,
    "Grid Frequency": false,
    "Grid Voltage": false,

    "Input Current": false,
    "Input VA": false,
    "Input Watt": false,
    //"Input Current 2": false,// EVO SP only
    //"Input VA 2": false,// EVO SP only
    //"Input Watt 2": false,// EVO SP only

    "Output Frequency": false,
    "Output Voltage": false,
    "Output Current": false,
    "Output VA": false,
    "Output Watt": false,
    //"Output Current 2": false,// EVO SP only
    //"Output VA 2": false,// EVO SP only
    //"Output Watt 2": false,// EVO SP only

    "Battery Voltage": false,
    "Battery Current": false,
    "External Current": false,

    "Battery Temperature": false,
    "Transformer Temperature": false,
    "Busbar Temperature": false,// EVO only
    "Heatsink Temperature": false,
    //"Heatsink Temperature 2": false,// EVO SP only
    "Fan Speed": false,

    //"CAN Watt": false,// EVO SP only
    //"Avg Watt": false// EVO SP only
  };

  const [selectedColumns, setSelectedColumns] = useState(
    EVOColumns
  );

  useEffect(() => {
    fetchLogData(); // Fetch data when the component mounts
  }, []); // Empty dependency array to run once when the component mounts

  const fetchLogData = async () => {
    try {
      setLogDataLoading(true);
      let tempLogDataArray = [];

      // Get the data for each log file
      for (let i = 0; i < selectedLogFiles.length; i++) {
        const response = await fetch(
          "https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/logs/get/data/" +
            selectedLogFiles[i].id
        );
        if (response.ok) {
          const dataBody = await response.json();
          const data = dataBody.data;
          tempLogDataArray.push(...data);
        } else {
          console.error("Failed to fetch data");
        }
      }

      setLogDataArray(tempLogDataArray); // Update the state
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLogDataLoading(false);
    }
  };

  const toggleColumnSelection = (columnName) => {
    // Prevent toggling the selection for "Date," "Time," and "Event" columns
    if (
      columnName === "Date" ||
      columnName === "Time" ||
      columnName === "Event"
    ) {
      return;
    }

    setSelectedColumns({
      ...selectedColumns,
      [columnName]: !selectedColumns[columnName],
    });
  };

  // Function to toggle the filter for 'Event' column
  const toggleEventFilter = () => {
    setShowOnlyNonBlankEvents(!showOnlyNonBlankEvents);
    console.log(showOnlyNonBlankEvents ? "true" : "false");
  };

  const bitMaskMapping = [
    "under frequency",
    "over frequency",
    "phase lock failure",
    "under voltage 1",
    "under voltage 2",
    "under voltage 3",
    "over voltage 1",
    "over voltage 2",
    "over voltage 3",
    "no VAC voltage",
    "input over current",
    "reserved",
    "reserved",
    "reserved",
    "reserved",
    "phase unlocked",
  ];

  const modeValues = [
    "standby/fault mode",
    "inverter mode",
    "charger mode",
    "power saving mode"
  ]

  const errorCodeValues = [
    "battery under voltage",      // 0000 0000 0000 0001
    "battery over voltage",       // 0000 0000 0000 0010
    "battery ultra low voltage",  // 0000 0000 0000 0100
    "input over current",         // 0000 0000 0000 1000

    "output over current",        // 0000 0000 0001 0000
    "output over load",           // 0000 0000 0010 0000
    "output short",               // 0000 0000 0100 0000
    "transformer over heat",      // 0000 0000 1000 0000

    "heat sink over heat 1",      // 0000 0001 0000 0000
    "output failure",             // 0000 0010 0000 0000
    "output over load 1",         // 0000 0100 0000 0000
    "output over load 2",         // 0000 1000 0000 0000

    "output over load 3",         // 0001 0000 0000 0000
    "output over load 4",         // 0010 0000 0000 0000
    "heat sink over heat 2",      // 0100 0000 0000 0000
    "stop output"                 // 1000 0000 0000 0000
  ]

const chargeStageValues = [
    "standby",
    "bulk current",
    "absorb voltage",
    "equalization voltage",
    "floating voltage",
    "Charger Off",
  ]


  const parseModeValues = (mode) => {
    return modeValues[mode]
  }

  const parseErrorCodeValues = (bitmask) => {
    const statuses = [];
    let value = 0;
    let bitCheck;

    errorCodeValues.forEach((status, index) => {
      bitCheck = 1 << index;
      if ((bitmask & bitCheck) !== 0) {
        value += bitCheck;
        statuses.push(status);
      }
    });
    return statuses;
  }

  const parseChargeStageValues = (charge) => {
    return chargeStageValues[charge]
  }

  const parseBitMask = (bitmask) => {
    const statuses = [];
    let value = 0;
    let bitCheck;

    // Check if "no VAC voltage" bit is on
    if ((bitmask & (1 << 9)) !== 0) {
      // Add only "no VAC voltage" to statuses and return
      return ["no VAC voltage (other bits ignored)"];
    }

    bitMaskMapping.forEach((status, index) => {
      bitCheck = 1 << index;
      if ((bitmask & bitCheck) !== 0) {
        value += bitCheck;
        statuses.push(status);
      }
    });
    return statuses;
  };

  // Function to handle 'View Log Data' button click
  const handleViewLogsChartClick = () => {
    navigate("/logs/chart/" + product.id, {
      state: { product, selectedLogFiles },
    });
  };

  // Function to handle 'Logs Home' button click
  const handleNavigateLogsHome = (product) => {
    navigate("/logs/" + product.id, { state: { product } });
  };

  // Function to handle 'My Products' button click
  const handleNavigateHome = () => {
    navigate("/product");
  };

  return (
    <div className="viewLogDataContainer">
      <div className="viewLogDataHeader">
        {LogDataLoading ? (
          <p>Loading...</p>
        ) : (
          <div className="viewLogDataHeaderTitle">
            <div className="viewLogDataProductType">{product.product_type}</div>
            <div className="viewLogDataProductSerialNumber">
              {product.product_serial_number}
            </div>
          </div>
        )}
        <div className="viewLogDataHeaderSearch">
          <div className="viewLogDataHeaderSearchIcon">
            <FontAwesomeIcon icon={faSearch} className="searchIcon" />
          </div>
          <div className="viewLogDataHeaderSearchInput">
            <input type="text" placeholder="Search/Filter..." />
          </div>
        </div>

        <div className="viewLogDataFunctionButtons">
          <div className="viewLogDataHeaderOperation">
            <button onClick={handleViewLogsChartClick}>
              <FontAwesomeIcon icon={faChartLine} />
              &nbsp; View Chart
            </button>
          </div>
          <div className="viewLogDataNavigateLogsHome">
            <button className="navigationButton" onClick={() => handleNavigateLogsHome(product)} >
              <FontAwesomeIcon icon={faFileText} />&nbsp;Log Files
            </button>
          </div>
          <div className="viewLogDataNavigateHome">
            <button className="navigationButton" onClick={() => handleNavigateHome()}>
              <FontAwesomeIcon icon={faHouse} />My Products
            </button>
          </div>
        </div>
      </div>
      {LogDataLoading ? (
        <p>Loading...</p>
      ) : (
        <div className="viewLogDataTableContent">
          <div className="tableScrollWrapper">
            <table className="viewLogDataTable">
              <thead>
                <tr>
                  {Object.keys(EVOColumns).map((columnName) => (
                    <th key={columnName}>
                      <label>
                        {columnName}
                        {columnName === "Event" && (
                          <span className="viewLogDataEventFilter" onClick={toggleEventFilter}>
                            <FontAwesomeIcon icon={faFilter} />
                          </span>
                        )}
                      </label>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {LogDataArray.length > 0 ? (
                  LogDataArray.filter(item => !showOnlyNonBlankEvents || item.event.trim() !== "").map((item, index) => {
                    return (
                      <tr key={index}>
                        <td>{item.date}</td>
                        <td>{item.time}</td>

                        <td>{parseModeValues(item.mode)}</td>
                        <td>{parseErrorCodeValues(item.error_code)}</td>
                        <td>{parseChargeStageValues(item.charge_stage)}</td>
                        <td>{item.event}</td>

                        <td>
                          {parseBitMask(item.gen_status).join(", ")}{" "}
                          [{item.gen_status}]
                        </td>
                        <td>{item.gen_freq}</td>
                        <td>{item.gen_volt}</td>

                        <td>
                          {parseBitMask(item.grid_status).join(", ")}{" "}
                          [{item.grid_status}]
                        </td>
                        <td>{item.grid_freq}</td>
                        <td>{item.grid_volt}</td>

                        <td>{item.input_current}</td>
                        <td>{item.input_va}</td>
                        <td>{item.input_watt}</td>

                        <td>{item.output_freq}</td>
                        <td>{item.output_volt}</td>
                        <td>{item.output_current}</td>
                        <td>{item.output_va}</td>
                        <td>{item.output_watt}</td>

                        <td>{item.battery_volt}</td>
                        <td>{item.battery_current}</td>
                        <td>{item.external_current}</td>

                        <td>{item.battery_temp}</td>
                        <td>{item.transformer_temp}</td>
                        <td>{item.busbar_temp}</td>
                        <td>{item.heatsink_temp}</td>
                        <td>{item.fan_speed}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="2">No data available</td>
                    <td>{LogDataArray.length}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewLogData;
